import Loader from '../../../../components/shared/loader';
import Pagination from '../../../../components/shared/pagination';
import { formatUTCTimestampToLocal } from '../../../../utils/set-date-range-utils';
import { camelCaseToWords } from '../../../../utils/structure-words-utils';
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react'
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';
import TabToggle from '../../../../components/shared/tab-toggle';
import { assignUserJob, unAssignUserJob, } from '../../../../services/jobs-service';

const Users = ({ data, loading, jobId, error, setData, globalFilter, onGlobalFilterChange, accountId, fetchUsers, statusOptions }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sorting, setSorting] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    // const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);


    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const closeMenu = () => {
        setOpenMenuId(null);
    };

    const handleRowClick = (row, e) => {
        if (!e.target.closest('input[type="checkbox"]')) {
            setSelectedRow(row.original);
            // setIsEditModalOpen(true);
        }
    };


    useEffect(() => {
        const handleClickOutside = () => closeMenu();
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Define columns with sorting configuration
    const columns = useMemo(
        () => [
            {
                id: 'select',
                header: ({ table }) => (
                    <div className="flex justify-center w-full">
                        <input
                            type="checkbox"
                            checked={table.getIsAllRowsSelected()}
                            onChange={(e) => {
                                e.stopPropagation();
                                table.getToggleAllRowsSelectedHandler()(e);
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                cell: ({ row }) => (
                    <div className="flex justify-center w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={row.getIsSelected()}
                            onChange={(e) => {
                                e.stopPropagation(); // Stop event bubbling
                                row.getToggleSelectedHandler()(e); // Toggle checkbox
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                enableSorting: false,
                size: 50,
            },
            {
                accessorKey: 'userId',
                header: 'User Id',
                enableSorting: true,
            },
            {
                accessorKey: 'firstName',
                header: 'First Name',
                enableSorting: true,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'lastName',
                header: 'Last Name',
                enableSorting: true,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'role',
                header: 'Role',
                enableSorting: true,
                cell: info => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'assigned',
                header: 'Assigned',
                enableSorting: false,
                cell: ({ row }) => {

                    const user = row.original;

                    const handleStatusChange = async (val) => {
                        const originalAssigned = user.assigned;
                        const newAssigned = val === 'On';

                        // Optimistically update the UI
                        setData(prevData =>
                            prevData.map(item =>
                                item.userId === user.userId
                                    ? { ...item, assigned: newAssigned }
                                    : item
                            )
                        );

                        try {
                            setIsUpdating(true);

                            if (newAssigned) {
                                const result = await assignUserJob(accountId, jobId, user.userId);
                                console.log("Assign Response : ", result)
                                if (!result.success) throw new Error(result.data.message || 'Failed to assign user');
                                setSuccessMessage('User assigned to job successfully');
                            } else {
                                const result = await unAssignUserJob(accountId, jobId, user.userId);
                                console.log("Un Assign Response : ", result)
                                if (result.status !== 204) throw new Error(result.data.message || 'Failed to unassign user');
                                setSuccessMessage('User unassigned from job successfully');
                            }

                        } catch (error) {
                            setErrorMessage(error.message || 'Something went wrong');

                            // Revert the UI if the API call fails
                            setData(prevData =>
                                prevData.map(item =>
                                    item.userId === user.userId
                                        ? { ...item, assigned: originalAssigned }
                                        : item
                                )
                            );
                        } finally {
                            setIsUpdating(false);
                        }
                    };


                    return (
                        <div className="flex justify-start items-center">
                            <TabToggle
                                options={[
                                    { label: 'Off', value: 'Off' },
                                    { label: 'On', value: 'On' },
                                ]}
                                className={'!w-14'} // Width to fit On/Off labels
                                selected={user.assigned ? 'On' : 'Off'}
                                onChange={handleStatusChange}
                                disabled={isUpdating || loading}
                            />
                            {isUpdating && (
                                <div className="ml-2">
                                    <Loader className="h-6 w-6" />
                                </div>
                            )}
                        </div>
                    );
                },
            },
        ],
        [openMenuId]
    );

    // Initialize the table
    const table = useReactTable({
        data, // Use the full dataset - no pre-filtering needed
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        enableSortingRemoval: false,
        // Add global filter function
        globalFilterFn: (row, columnId, filterValue) => {
            const searchLower = filterValue.toLowerCase();
            const rowValue = String(row.getValue(columnId)).toLowerCase();

            if (columnId === 'expenseTime') {
                return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
            } else if (columnId.includes('.')) {
                const [parent, child] = columnId.split('.');
                const value = row.original[parent]?.[child] || '';
                return String(value).toLowerCase().includes(searchLower);
            }

            return rowValue.includes(searchLower);
        },
    });

    return (
        <div className='w-full'>
            {successMessage && (
                <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
                    {errorMessage}
                </div>
            )}
            <div className="overflow-x-auto max-w-full pb-16">
                {loading ? (
                    <Loader />
                ) : (
                    <table className="min-w-full text-sm border border-gray-200">
                        <colgroup>
                            <col style={{ width: '50px' }} />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col style={{ width: '50px' }} />
                        </colgroup>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            className="px-2 py-1.5 border-b capitalize"
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                width: header.column.getSize(),
                                            }}
                                        >
                                            <div className="flex items-center gap-1">
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {header.column.getCanSort() && (
                                                    <span className="text-black">
                                                        {{
                                                            asc: <RiArrowUpSLine className="text-base mt-0.5" />,
                                                            desc: <RiArrowUpSLine className="text-base rotate-180" />,
                                                        }[header.column.getIsSorted()] ?? (
                                                                <RxCaretSort className="text-lg" />
                                                            )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map(row => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={(e) => handleRowClick(row, e)}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td
                                                key={cell.id}
                                                className="px-2 py-1 border-b whitespace-nowrap"
                                                style={{ width: cell.column.getSize() }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Pagination
                currentPage={currentPage}
                totalItems={table.getFilteredRowModel().rows.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(count) => {
                    setItemsPerPage(count);
                    setCurrentPage(1);
                }}
            />
        </div>
    )
}

export default Users
